import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

// Platform release switches. These are not per-tenant gates — tenant_modules
// already does that — they answer whether a feature exists on the platform at
// all, so unfinished work can ship to production switched off.
//
// Read from an unauthenticated, allow-listed endpoint because the signup page
// needs them before anyone has logged in.
export const FEATURE_FLAGS = {
  esl: 'esl_module_enabled',
  companion: 'companion_module_enabled',
} as const;

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['public-feature-flags'],
    queryFn: () => api.get<{ data: Record<string, boolean> }>('/public/feature-flags'),
    // Changes roughly never, and a stale read here would only briefly show or
    // hide a signup option.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Whether shelf labels exist on this platform right now. Defaults to FALSE while
 * loading and on error: a switch that fails open would expose an unreleased
 * feature the first time the endpoint hiccuped.
 */
export function useEslPlatformEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.data?.[FEATURE_FLAGS.esl] === true;
}

/** Whether Campaign API (per-location SMS/email API, formerly Companion) is
 *  released on this platform right now. Same fail-closed default as the ESL
 *  switch. */
export function useCampaignApiPlatformEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.data?.[FEATURE_FLAGS.companion] === true;
}
